import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  ShoppingCart, Cloud, Users, Euro, TrendingUp,
  ArrowRight, Loader2, CheckCircle2, Clock, AlertCircle
} from 'lucide-react';

export const AdminDashboard = () => {
  const { token } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/admin/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data.stats);
          setRecentOrders(data.recent_orders);
        }
      } catch (error) {
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token, backendUrl]);

  const getStatusBadge = (status) => {
    const configs = {
      'paid': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle },
    };
    return configs[status] || configs['pending'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Überblick über das Wolkenhelden-System</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Umsatz</CardTitle>
            <Euro className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">
              {stats?.revenue?.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </div>
            <p className="text-xs text-slate-500 mt-1">Gesamtumsatz (bezahlte Bestellungen)</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Bestellungen</CardTitle>
            <ShoppingCart className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.orders?.total || 0}</div>
            <div className="flex gap-2 mt-1">
              <span className="text-xs text-green-400">{stats?.orders?.paid || 0} bezahlt</span>
              <span className="text-xs text-yellow-400">{stats?.orders?.pending || 0} ausstehend</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Clouds</CardTitle>
            <Cloud className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.instances?.total || 0}</div>
            <p className="text-xs text-green-400 mt-1">{stats?.instances?.active || 0} aktiv</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Kunden</CardTitle>
            <Users className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.customers?.total || 0}</div>
            <p className="text-xs text-slate-500 mt-1">{stats?.users?.total || 0} registrierte Benutzer</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Letzte Bestellungen</CardTitle>
          <Link to="/admin/orders" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
            Alle anzeigen <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentOrders.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Noch keine Bestellungen</p>
            ) : (
              recentOrders.map((order) => {
                const statusConfig = getStatusBadge(order.status);
                const StatusIcon = statusConfig.icon;
                return (
                  <Link
                    key={order.id}
                    to={`/admin/orders/${order.id}`}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-white font-medium">{order.customer_name || order.customer_email || 'Unbekannt'}</p>
                        <p className="text-slate-400 text-sm">
                          {new Date(order.created_at).toLocaleDateString('de-DE', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-white font-semibold">
                        {order.total_brutto?.toFixed(2)} €
                      </span>
                      <Badge className={statusConfig.class}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {order.status}
                      </Badge>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
